/**
 * Copyright © 2026 Delicious AI, LLC
 */

import { ContentCopy, OpenInFull } from '@mui/icons-material'
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Chip,
  IconButton,
  Snackbar,
  Stack,
  Tooltip,
} from '@mui/material'
import React from 'react'

import type { UltraDAIZeroReviewCropFieldsFragment } from '@dai/graphql'

import useT from '@/i18n/useT'

import { getCropImageStyle, getCropOverlayStyle } from './classificationUtils'

type DAIZeroReviewCropCardProps = {
  crop: UltraDAIZeroReviewCropFieldsFragment
  selected: boolean
  size: number
  onSelect: (shiftKey: boolean) => void
  onOpen: () => void
}

const DAIZeroReviewCropCard: React.FC<DAIZeroReviewCropCardProps> = ({
  crop,
  selected,
  size,
  onSelect,
  onOpen,
}) => {
  const { t } = useT()
  const [naturalSize, setNaturalSize] = React.useState<{
    width: number
    height: number
  } | null>(null)
  const [copyFailed, setCopyFailed] = React.useState(false)
  const cropViewport = React.useMemo(
    () => ({ width: size, height: size }),
    [size],
  )
  const cropOverlayStyle = naturalSize
    ? getCropOverlayStyle(crop, naturalSize, cropViewport)
    : undefined

  const copyCropId = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error('Clipboard API unavailable')
      }
      await navigator.clipboard.writeText(crop.cropId)
    } catch (error) {
      console.error('Failed to copy crop ID to clipboard', error)
      setCopyFailed(true)
    }
  }

  return (
    <>
      <Card
        onClick={event => onSelect(event.shiftKey)}
        sx={{
          width: size,
          cursor: 'pointer',
          outline: selected ? '3px solid' : 'none',
          outlineColor: 'primary.main',
        }}
      >
        <Box
          sx={{
            width: size,
            height: size,
            overflow: 'hidden',
            position: 'relative',
          }}
        >
          <Box
            component="img"
            src={crop.imageUrl}
            loading="lazy"
            decoding="async"
            alt={t('daiZeroReview.cropCard.imageAlt', {
              cropId: crop.cropId,
              defaultValue: `Crop ${crop.cropId}`,
            })}
            onLoad={event =>
              setNaturalSize({
                width: event.currentTarget.naturalWidth,
                height: event.currentTarget.naturalHeight,
              })
            }
            sx={
              naturalSize
                ? getCropImageStyle(crop, naturalSize, cropViewport)
                : { width: '100%', height: '100%', objectFit: 'cover' }
            }
          />
          {cropOverlayStyle && <Box sx={cropOverlayStyle} />}
          <Checkbox
            checked={selected}
            onClick={event => event.stopPropagation()}
            onChange={() => onSelect(false)}
            inputProps={{
              'aria-label': t('daiZeroReview.cropCard.selectAriaLabel', {
                cropId: crop.cropId,
                defaultValue: `Select crop ${crop.cropId}`,
              }),
            }}
            sx={{ position: 'absolute', top: 0, left: 0 }}
          />
          <Tooltip
            title={t(
              'daiZeroReview.cropCard.inspectTooltip',
              'Inspect source image',
            )}
          >
            <IconButton
              aria-label={t('daiZeroReview.cropCard.inspectAriaLabel', {
                cropId: crop.cropId,
                defaultValue: `Inspect crop ${crop.cropId} in its source image`,
              })}
              onClick={event => {
                event.stopPropagation()
                onOpen()
              }}
              size="small"
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                color: 'common.white',
                bgcolor: 'rgba(0, 0, 0, 0.55)',
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.75)' },
              }}
            >
              <OpenInFull fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Stack spacing={0.5} sx={{ p: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
          >
            <Chip
              size="small"
              label={
                crop.source === 'MLULTRA'
                  ? t('daiZeroReview.cropCard.source.mlUltra', 'MLUltra')
                  : t('daiZeroReview.cropCard.source.historical', 'Historical')
              }
            />
            <Tooltip
              title={t('daiZeroReview.cropCard.copyTooltip', 'Copy crop ID')}
            >
              <IconButton
                aria-label={t('daiZeroReview.cropCard.copyAriaLabel', {
                  cropId: crop.cropId,
                  defaultValue: `Copy crop ID ${crop.cropId}`,
                })}
                onClick={event => {
                  event.stopPropagation()
                  void copyCropId()
                }}
                size="small"
              >
                <ContentCopy fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          {(crop.excluded ||
            crop.replacementUpc ||
            (crop.source === 'MLULTRA' && crop.verified !== true)) && (
            <Stack direction="row" spacing={0.5} flexWrap="wrap">
              {crop.excluded && (
                <Chip
                  size="small"
                  color="warning"
                  label={t(
                    'daiZeroReview.cropCard.pendingExclusion',
                    'Pending exclusion',
                  )}
                />
              )}
              {crop.replacementUpc && (
                <Chip
                  size="small"
                  color="info"
                  label={t('daiZeroReview.cropCard.pendingRelabel', {
                    upc: crop.replacementUpc,
                    defaultValue: `Pending relabel · ${crop.replacementUpc}`,
                  })}
                />
              )}
              {crop.source === 'MLULTRA' && crop.verified === false && (
                <Chip
                  size="small"
                  color="secondary"
                  label={t('daiZeroReview.cropCard.unverified', 'Unverified')}
                />
              )}
              {crop.source === 'MLULTRA' && crop.verified === null && (
                <Chip
                  size="small"
                  color="default"
                  label={t(
                    'daiZeroReview.cropCard.unavailable',
                    'Unavailable in this environment',
                  )}
                />
              )}
            </Stack>
          )}
        </Stack>
      </Card>
      <Snackbar
        open={copyFailed}
        autoHideDuration={4000}
        onClose={() => setCopyFailed(false)}
      >
        <Alert severity="error" onClose={() => setCopyFailed(false)}>
          {t('daiZeroReview.cropCard.copyFailed', 'Could not copy crop ID.')}
        </Alert>
      </Snackbar>
    </>
  )
}

export default DAIZeroReviewCropCard
